import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Upload, Trash2, Pencil, Zap } from "lucide-react";
import { api, fmtErr, fmtRent, bedsLabel } from "../../lib/api";
import QuickAdd from "./QuickAdd";

const FIELDS = ["address_street", "unit_number", "city", "province", "postal_code", "neighborhood", "latitude", "longitude", "beds", "baths", "square_feet", "building_type", "monthly_rent", "date_available", "minimum_lease_months", "lease_type", "furnished", "pet_friendly", "parking", "laundry", "accessibility", "outdoor_space", "year_built", "company_name", "landlord_contact_name", "landlord_contact_email", "landlord_contact_phone", "description", "source_name", "source_url"];

const PLAN_LABELS = { per_listing: "Per Listing", starter: "Starter", growth: "Growth", unlimited: "Unlimited" };
const PAYMENT_LABELS = { free_trial: "Free trial", payment_confirmed: "Paid", unpaid: "Unpaid" };

export default function Inventory() {
  const [listings, setListings] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [showCsv, setShowCsv] = useState(false);
  const [showQuick, setShowQuick] = useState(false);

  const load = () => api.get("/admin/listings", { params: { ...(q ? { q } : {}), ...(status ? { status } : {}) } })
    .then((r) => setListings(r.data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, [q, status]); // eslint-disable-line

  const remove = async (id) => {
    if (!window.confirm("Mark this listing as removed?")) return;
    try { await api.delete(`/admin/listings/${id}`); load(); } catch (e) { toast.error(fmtErr(e)); }
  };

  const setStatusQuick = async (id, newStatus) => {
    try { await api.patch(`/admin/listings/${id}/status`, { status: newStatus }); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const publishToggle = async (id, published) => {
    try { await api.post(`/admin/listings/${id}/publish`, { published }); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="inventory-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight">Inventory</h1>
          <p className="text-sm text-muted-foreground">{listings.length} listings</p>
        </div>
        <div className="flex gap-2">
          <input data-testid="inventory-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search address, area, company"
            aria-label="Search inventory" className="rounded-xl border border-input bg-background px-4 py-2 text-sm w-56" />
          <select data-testid="inventory-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter status"
            className="rounded-xl border border-input bg-background px-3 py-2 text-sm">
            <option value="">All statuses</option>
            {["active", "draft", "leased", "pending", "expired", "removed", "duplicate"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button data-testid="quick-add-open" onClick={() => setShowQuick(true)}
            className="inline-flex items-center gap-1.5 rounded-full bg-accent text-accent-foreground px-4 py-2 text-sm font-semibold hover:bg-coral-600"><Zap className="w-4 h-4" /> Quick add</button>
          <button data-testid="csv-import-open" onClick={() => setShowCsv(true)}
            className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-semibold hover:bg-secondary"><Upload className="w-4 h-4" /> CSV import</button>
          <Link to="/dashboard/inventory/new" data-testid="inventory-add"
            className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold"><Plus className="w-4 h-4" /> Add listing</Link>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-5 py-3">Address</th><th className="px-5 py-3">Rent</th><th className="px-5 py-3">Beds/Baths</th>
              <th className="px-5 py-3">Source</th><th className="px-5 py-3">Quality</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Updated</th>
              <th className="px-5 py-3">Plan</th><th className="px-5 py-3">Payment</th><th className="px-5 py-3">Trial end</th><th className="px-5 py-3">Published</th><th className="px-5 py-3">Company</th><th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {listings.map((l) => (
              <tr key={l.listing_id} data-testid={`inventory-row-${l.listing_id}`} className="border-b border-border/60 last:border-0 hover:bg-secondary/40">
                <td className="px-5 py-3">
                  <p className="font-medium">{l.address_street}{l.unit_number ? ` #${l.unit_number}` : ""}</p>
                  <p className="text-xs text-muted-foreground">{l.neighborhood}</p>
                </td>
                <td className="px-5 py-3 font-semibold">{fmtRent(l.monthly_rent)}</td>
                <td className="px-5 py-3">{bedsLabel(l)} · {l.baths}ba</td>
                <td className="px-5 py-3 text-xs">
                  {l.source_name}
                  {(l.sources?.length || 0) > 1 && <span className="ml-1 rounded-full bg-teal-100 dark:bg-teal-900 text-teal-900 dark:text-teal-100 px-1.5 py-0.5 font-semibold" title="De-duplicated across sources">×{l.sources.length}</span>}
                </td>
                <td className="px-5 py-3"><span className="font-mono text-xs">{l.quality_score}</span></td>
                <td className="px-5 py-3">
                  <select data-testid={`inventory-status-${l.listing_id}`} value={l.status} onChange={(e) => setStatusQuick(l.listing_id, e.target.value)} aria-label="Listing status"
                    className="rounded-lg border border-input bg-background px-2 py-1 text-xs">
                    {["active", "draft", "leased", "expired", "pending", "removed", "duplicate"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-5 py-3 text-xs text-muted-foreground whitespace-nowrap">{l.updated_at?.slice(0, 10) || "—"}</td>
                <td className="px-5 py-3 text-xs">{PLAN_LABELS[l.plan] || "—"}</td>
                <td className="px-5 py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${l.payment_status === "payment_confirmed" ? "bg-green-100 text-green-900 dark:bg-green-900 dark:text-green-100" : l.payment_status === "free_trial" ? "bg-teal-100 text-teal-900 dark:bg-teal-900 dark:text-teal-100" : "bg-secondary"}`}>{PAYMENT_LABELS[l.payment_status] || "—"}</span></td>
                <td className="px-5 py-3 text-xs text-muted-foreground whitespace-nowrap">{l.free_trial_end || "—"}</td>
                <td className="px-5 py-3">
                  <button data-testid={`inventory-publish-${l.listing_id}`} onClick={() => publishToggle(l.listing_id, !l.published)} aria-label={l.published ? "Unpublish" : "Publish"}
                    className={`rounded-full px-2.5 py-1 text-xs font-semibold ${l.published ? "bg-green-100 text-green-900 dark:bg-green-900 dark:text-green-100" : "bg-secondary text-muted-foreground"}`}>
                    {l.published ? "Live" : "Hidden"}
                  </button>
                </td>
                <td className="px-5 py-3 text-xs">{l.company_name}</td>
                <td className="px-5 py-3">
                  <div className="flex gap-1">
                    <Link to={`/dashboard/inventory/${l.listing_id}/edit`} data-testid={`inventory-edit-${l.listing_id}`} aria-label="Edit listing"
                      className="p-1.5 rounded-lg hover:bg-secondary"><Pencil className="w-4 h-4" /></Link>
                    <button data-testid={`inventory-delete-${l.listing_id}`} onClick={() => remove(l.listing_id)} aria-label="Delete listing"
                      className="p-1.5 rounded-lg hover:bg-coral-50 text-coral-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!listings.length && <tr><td colSpan={13} className="px-5 py-10 text-center text-muted-foreground" data-testid="inventory-empty">No listings yet — add your first with Quick add, the full form, or a CSV import.</td></tr>}
          </tbody>
        </table>
      </div>

      {showCsv && <CsvImport onClose={() => { setShowCsv(false); load(); }} />}
      {showQuick && <QuickAdd onClose={() => setShowQuick(false)} onSaved={() => { setShowQuick(false); load(); }} />}
    </div>
  );
}

function guessField(h) {
  const n = h.toLowerCase().replace(/[\s\-]+/g, "_");
  if (FIELDS.includes(n)) return n;
  const map = { address: "address_street", street: "address_street", unit: "unit_number", rent: "monthly_rent", price: "monthly_rent", bedrooms: "beds", bathrooms: "baths", sqft: "square_feet", area: "neighborhood", email: "landlord_contact_email", phone: "landlord_contact_phone", company: "company_name", available: "date_available" };
  return map[n] || "";
}

function CsvImport({ onClose }) {
  const [text, setText] = useState("");
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const parseHeaders = (t) => {
    setText(t);
    const first = t.split("\n")[0] || "";
    const hs = first.split(",").map((h) => h.trim().replace(/^"|"$/g, "")).filter(Boolean);
    setHeaders(hs);
    const m = {};
    hs.forEach((h) => { m[h] = guessField(h); });
    setMapping(m);
  };

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/listings/import-csv", { csv_text: text, mapping });
      setResult(r.data);
      toast.success(`Imported: ${r.data.added} added, ${r.data.updated} merged`);
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="CSV import">
      <div className="w-full max-w-2xl rounded-2xl border border-border bg-card p-6 max-h-[85vh] overflow-y-auto">
        <h2 className="font-heading font-semibold text-xl">CSV / bulk import</h2>
        <p className="mt-1 text-xs text-muted-foreground">Paste CSV with a header row, then map columns to listing fields. Duplicates are merged automatically.</p>
        <textarea data-testid="csv-text" value={text} onChange={(e) => parseHeaders(e.target.value)} rows={6}
          placeholder="address_street,neighborhood,beds,baths,monthly_rent,…&#10;123 Maple St,Kitsilano,1,1,2250,…"
          className="mt-4 w-full rounded-xl border border-input bg-background px-4 py-3 text-xs font-mono" />
        {headers.length > 0 && (
          <div className="mt-4 grid grid-cols-2 gap-2">
            {headers.map((h) => (
              <div key={h} className="flex items-center gap-2 text-xs">
                <span className="font-mono w-32 truncate" title={h}>{h}</span>
                <select data-testid={`csv-map-${h}`} value={mapping[h] || ""} onChange={(e) => setMapping({ ...mapping, [h]: e.target.value })}
                  className="flex-1 rounded-lg border border-input bg-background px-2 py-1.5" aria-label={`Map column ${h}`}>
                  <option value="">— skip —</option>
                  {FIELDS.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}
        {result && (
          <div data-testid="csv-result" className="mt-4 rounded-xl bg-secondary px-4 py-3 text-sm">
            {result.added} added · {result.updated} merged · {result.failed} failed
            {result.errors?.map((e, i) => <p key={i} className="text-xs text-coral-600 mt-1">{e}</p>)}
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button data-testid="csv-cancel" onClick={onClose} className="rounded-full border border-border px-5 py-2 text-sm font-medium">Close</button>
          <button data-testid="csv-run" onClick={run} disabled={busy || !headers.length}
            className="rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-semibold disabled:opacity-40">Import</button>
        </div>
      </div>
    </div>
  );
}
